import { createContext, useContext, type ReactNode } from 'react'
import { useFeedback, type UseFeedbackReturn } from './useFeedback'
import { FeedbackDialog } from './FeedbackDialog'

/**
 * Shares the single feedback session (capture state + open/close/submit) with
 * every trigger in the shell. Both the desktop header button and the mobile
 * sidebar-footer button must drive the SAME `useFeedback()` instance —
 * calling the hook twice would give each trigger its own screenshot state and
 * its own dialog. The provider also owns the one `<FeedbackDialog>`.
 */
const FeedbackContext = createContext<UseFeedbackReturn | null>(null)

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const feedback = useFeedback()

  return (
    <FeedbackContext.Provider value={feedback}>
      {children}
      <FeedbackDialog
        open={feedback.isOpen}
        onOpenChange={(v) => {
          if (!v) feedback.close()
        }}
        screenshot={feedback.screenshot}
        video={feedback.video}
        isCapturing={feedback.isCapturing}
        onStartRecording={feedback.startRecording}
        onSubmit={feedback.submit}
      />
    </FeedbackContext.Provider>
  )
}

/** Like useFeedbackContext but returns null outside a provider (crash screens). */
export function useOptionalFeedbackContext(): UseFeedbackReturn | null {
  return useContext(FeedbackContext)
}

export function useFeedbackContext(): UseFeedbackReturn {
  const ctx = useContext(FeedbackContext)
  if (!ctx) {
    throw new Error('useFeedbackContext must be used inside a <FeedbackProvider>')
  }
  return ctx
}
